/**
 * Google Analytics 4 (GA4) Analytics Utility
 * GDPR-friendly setup - only loads after cookie consent
 * No ad personalisation or remarketing
 */

// GA4 Measurement ID - configurable via environment variable
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

// Cookie consent key in localStorage
const CONSENT_KEY = 'analytics_consent';

/**
 * Check if user has given analytics consent
 */
export const hasAnalyticsConsent = () => {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(CONSENT_KEY) === 'granted';
};

/**
 * Set analytics consent status
 */
export const setAnalyticsConsent = (granted) => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(CONSENT_KEY, granted ? 'granted' : 'denied');
  
  if (granted) {
    // Update consent mode first
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'granted'
      });
    }
    // Load the GA4 script directly (initializeGA4 may have already run
    // on app mount and set gaInitialized=true, so we bypass it here)
    loadGA4Script();
  } else {
    // If consent revoked, update GA4 consent mode
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied'
      });
    }
    // Remove the GA4 script tag if it was loaded
    removeGA4Script();
  }
};

/**
 * Check if consent has been explicitly set (not just missing)
 */
export const hasConsentBeenSet = () => {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(CONSENT_KEY) !== null;
};

/**
 * Initialize GA4 with GDPR-friendly default consent mode
 */
export const initializeGA4 = () => {
  if (typeof window === 'undefined' || !GA_MEASUREMENT_ID) return;
  
  // Don't initialize if already loaded
  if (window.gaInitialized) return;
  
  // Check consent before loading
  const consentGranted = hasAnalyticsConsent();
  
  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  
  // Set default consent mode (GDPR requirement)
  gtag('consent', 'default', {
    analytics_storage: consentGranted ? 'granted' : 'denied',
    ad_storage: 'denied', // No ads
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500
  });
  
  // Only load GA4 script if consent granted
  if (consentGranted) {
    loadGA4Script();
  }
  
  window.gaInitialized = true;
};

/**
 * Load GA4 script dynamically — only when consent is granted
 */
const loadGA4Script = () => {
  if (!GA_MEASUREMENT_ID) return;
  
  // Check if already loaded
  if (document.querySelector(`script[src*="${GA_MEASUREMENT_ID}"]`)) return;
  
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  script.id = 'ga4-script';
  document.head.appendChild(script);
  
  script.onload = () => {
    window.gtag('js', new Date());
    window.gtag('config', GA_MEASUREMENT_ID, {
      anonymize_ip: true, // GDPR: Anonymize IP addresses
      allow_google_signals: false, // No remarketing
      allow_ad_personalization_signals: false // No ad personalization
    });
  };
};

/**
 * Remove GA4 script when consent is revoked
 */
const removeGA4Script = () => {
  const script = document.getElementById('ga4-script');
  if (script) script.remove();
};

/**
 * Track page view
 */
export const trackPageView = (pagePath, pageTitle) => {
  if (!hasAnalyticsConsent() || !window.gtag) return;
  
  window.gtag('event', 'page_view', {
    page_path: pagePath,
    page_title: pageTitle
  });
};

/**
 * Track custom event
 */
export const trackEvent = (eventName, parameters = {}) => {
  if (!hasAnalyticsConsent() || !window.gtag) return;
  
  window.gtag('event', eventName, {
    ...parameters,
    send_to: GA_MEASUREMENT_ID
  });
};

// ========== BOOKING CONVERSION EVENTS ==========

/**
 * Track when user views the booking page
 */
export const trackBookingPageView = (serviceId, serviceName) => {
  trackEvent('view_booking_page', {
    event_category: 'booking',
    service_id: serviceId,
    service_name: serviceName
  });
};

/**
 * Track when user reaches Stripe checkout
 */
export const trackCheckoutStarted = (bookingData) => {
  trackEvent('reached_stripe_checkout', {
    event_category: 'booking',
    service_id: bookingData.serviceId,
    service_name: bookingData.serviceName,
    therapist_id: bookingData.therapistId,
    booking_date: bookingData.date,
    value: bookingData.price,
    currency: 'GBP'
  });
};

/**
 * Track when booking is completed successfully
 */
export const trackBookingCompleted = (bookingData) => {
  trackEvent('booking_completed', {
    event_category: 'booking',
    transaction_id: bookingData.bookingId,
    service_id: bookingData.serviceId,
    service_name: bookingData.serviceName,
    therapist_id: bookingData.therapistId,
    value: bookingData.price,
    currency: 'GBP'
  });
  
  // Also track as a conversion event for GA4
  trackEvent('conversion', {
    send_to: GA_MEASUREMENT_ID,
    value: bookingData.price,
    currency: 'GBP',
    transaction_id: bookingData.bookingId
  });
};

/**
 * Track booking abandonment (if user leaves before completing)
 */
export const trackBookingAbandoned = (step, bookingData = {}) => {
  trackEvent('booking_abandoned', {
    event_category: 'booking',
    abandonment_step: step,
    service_id: bookingData.serviceId,
    service_name: bookingData.serviceName
  });
};

export default {
  initializeGA4,
  hasAnalyticsConsent,
  setAnalyticsConsent,
  hasConsentBeenSet,
  trackPageView,
  trackEvent,
  trackBookingPageView,
  trackCheckoutStarted,
  trackBookingCompleted,
  trackBookingAbandoned
};
